/**
 * The user's survey-number layer choice, remembered across maps and visits.
 *
 * Once someone turns survey numbers on, every map opens with them on (and the
 * "Enable survey numbers" nudge never shows again); if they turn them off, they
 * stay off. All maps on the page share it live, so switching tabs on the Maps
 * page or opening the request pop-up never asks again.
 *
 * The Survey Data (Measure) map is the exception: it ALWAYS opens with the
 * numbers on, they are the point of that map, whatever was stored. Turning
 * them off there lasts for that visit (and, like any toggle, becomes the
 * stored choice for the other maps).
 */
import { useCallback, useEffect, useRef, useState } from "react";

const KEY = "jg_survey_layer_on";
const EVENT = "jg-survey-layer-change";

export function readSurveyLayerOn() {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

/** Has the user ever made a choice (turned the layer on or off)? */
export function hasSurveyLayerChoice() {
  try {
    return localStorage.getItem(KEY) !== null;
  } catch {
    return false;
  }
}

export function writeSurveyLayerOn(on) {
  try {
    localStorage.setItem(KEY, on ? "1" : "0");
  } catch {
    /* private mode: the choice lasts for this page only */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: !!on }));
}

/**
 * [on, toggle, setOn] — shared, persisted survey-layer state for a map.
 * `startOn` makes this map open with the layer on regardless of the stored
 * choice (the Measure map); the stored choice is read otherwise.
 */
export function useSurveyLayerOn(startOn = false) {
  const [on, setOnState] = useState(() => startOn || readSurveyLayerOn());
  const onRef = useRef(on);
  onRef.current = on;
  useEffect(() => {
    const sync = (e) => setOnState(Boolean(e.detail));
    const fromOtherTab = (e) => e.key === KEY && setOnState(e.newValue === "1");
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", fromOtherTab);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", fromOtherTab);
    };
  }, []);
  const setOn = useCallback((v) => writeSurveyLayerOn(v), []);
  // Flip what THIS map shows (not the stored value, which can differ on a
  // map that started on).
  const toggle = useCallback(() => writeSurveyLayerOn(!onRef.current), []);
  return [on, toggle, setOn];
}
